import {Catalog} from "../models/Catalog";
import {Space} from "../models/Space";
import {Markup} from "../models/Markup";
import {Boundary} from "../models/Boundary";
import {Xyicon} from "../models/Xyicon";
import {SpaceVersion} from "../models/SpaceVersion";
import {LibraryImage} from "../models/LibraryImage";
import type {AppState} from "../state/AppState";
import type {FeatureMap} from "../state/AppStateConstants";
import {Link} from "../models/Link";
import {LibraryModel} from "../models/LibraryModel";
import type {IError, TransportLayer} from "../TransportLayer";
import {XHRCancelToken, XHRLoader} from "../../utils/loader/XHRLoader";
import {Portfolio} from "../models/Portfolio";
import type {
	DeleteBoundaryRequest,
	DeleteBoundarySpaceMapRequest,
	DeleteDocumentsRequest,
	DeleteLibraryImagesRequest,
	DeleteLibraryModelsRequest,
	DeleteMarkupRequest,
	DeletePortfolioGroupRequest,
	DeletePortfolioPermissionSetRequest,
	DeletePortfolioRequest,
	DeleteSpaceRequest,
	DeleteSpaceVersionRequest,
	DeleteUserGroupRequest,
	DeleteUsersRequest,
	DeleteXyiconCatalogRequest,
	DeleteXyiconRequest,
	PortfolioDto,
	SpaceDto,
	UpdateBoundaryFieldsRequest,
	UpdateSpaceFieldsRequest,
	UpdateXyiconFieldsRequest,
	UpdateXyiconsModelRequest,
	XyiconCatalogMappingInfo,
	XyiconDto,
} from "../../generated/api/base";
import {Permission, ViewPreferenceCategory, XyiconFeature} from "../../generated/api/base";
import type {IModel} from "../models/Model";
import type {IDefaultGlyphData} from "../../ui/modules/catalog/create/CatalogTypes";
import {ObjectUtils} from "../../utils/data/ObjectUtils";
import {PortfolioGroup} from "../models/PortfolioGroup";
import {UserGroup} from "../models/UserGroup";
import {ExternalUser, User} from "../models/User";
import {PermissionSet} from "../models/permission/PermissionSet";
import {DocumentModel} from "../models/DocumentModel";
import {Report} from "../models/Report";
import type {
	ReportArchiveDownloadDto,
	DeleteReportRequest,
	GetReportArchiveDownloadRequest,
	GetReportArchiveRequest,
	InvokeReportRequest,
	InvokeSavedReportDto,
	InvokeUnsavedReportDto,
	InvokeUnsavedReportRequest,
	ReportArchiveDto,
	ReportDto,
	UpdateReportRequest,
	GetDistinctBoundaryFieldValuesSelectedQueryResult,
	GetAllSpacesForPortfolioQueryResult,
	PortfolioSpacesDto,
} from "../../generated/api/reports";
import {ReportScope} from "../../generated/api/reports";
import {Dashboard} from "../models/Dashboard";
import {doesItemExistInViewFolderStructure} from "../models/ViewUtils";
import type {ICustomUserInput} from "../../ui/5.0/modules/report/create/custom-inputs/CustomReportInputV5";
import {EventNameForViewsChangeInLocalStorage} from "../../ui/5.0/topbar/ViewTabsCommon";

const apiNames: FeatureMap<string> = {
	[XyiconFeature.Portfolio]: "portfolios",
	[XyiconFeature.PortfolioGroup]: "portfoliogroups",
	[XyiconFeature.XyiconCatalog]: "xyiconcatalogs",
	[XyiconFeature.Xyicon]: "xyicons",
	[XyiconFeature.Space]: "spaces",
	[XyiconFeature.SpaceVersion]: "spaceversions",
	[XyiconFeature.Markup]: "markups",
	[XyiconFeature.Boundary]: "boundaries",
	[XyiconFeature.LibraryImage]: "libraryimages",
	[XyiconFeature.LibraryModel]: "librarymodels",
	[XyiconFeature.Dashboard]: "dashboards",
	[XyiconFeature.UserGroup]: "usergroups",
	[XyiconFeature.User]: "users",
	[XyiconFeature.ExternalUser]: "users/external",
	[XyiconFeature.PermissionSet]: "portfoliopermissionsets",
	[XyiconFeature.Document]: "documents",
	[XyiconFeature.PortfolioDocument]: "documents/portfolio",
	[XyiconFeature.OrganizationDocument]: "documents/organization",
	[XyiconFeature.Report]: "reports",
	[XyiconFeature.Link]: "links",
};

export enum MappingType {
	ConvertToDirectLink = 1,
	ConvertToEmbeddedLink = 2, // we removed this option from the UI with #3146
	MapToPort = 3,
	BreakLink = -1,
}

export interface IPortMapping {
	[fromPortId: string]: {
		mappingType: MappingType;
		port: string | null;
	};
}

export type XyiconMappingInformation = Record<string, XyiconCatalogMappingInfo> | null;

export class FeatureService {
	private readonly _transport: TransportLayer;
	private _promises: {
		[key in XyiconFeature]?: Promise<IModel[]>;
	} = {};
	private _dependencyPromises: {
		[key in XyiconFeature]?: Promise<IModel[]>;
	} = {};
	private readonly _cancelTokens = new Set<XHRCancelToken>();

	constructor(transportLayer: TransportLayer) {
		this._transport = transportLayer;
	}

	public static getApiNameForFeature(feature: XyiconFeature, isRefreshList?: boolean) {
		const apiNameFeature =
			!isRefreshList && [XyiconFeature.OrganizationDocument, XyiconFeature.PortfolioDocument].includes(feature) ? XyiconFeature.Document : feature;
		const apiName = apiNames[apiNameFeature];

		if (!apiName) {
			throw new Error(`Feature not defined! ${feature}`);
		}
		return apiName;
	}

	public async getArchiveReports(reportID: string) {
		const params: GetReportArchiveRequest = {
			organizationID: this._transport.appState.organizationIdByAuthData,
			userID: this._transport.appState.user.id,
			portfolioID: this._transport.appState.activePortfolioId,
			reportID: reportID,
		};

		const {result, error} = await this._transport.requestForOrganization<ReportArchiveDto[]>(
			{
				url: "reports/archives/all",
				method: XHRLoader.METHOD_POST,
				params: params,
			},
			"reports",
		);

		return result;
	}

	public async getAllReportViews() {
		const appState = this._transport.appState;
		const {result: reportViewDataArray, error} = await this._transport.requestForOrganization<ReportDto[]>(
			{
				url: "reports/all",
				method: XHRLoader.METHOD_GET,
				params: {
					userID: appState.user.id,
				},
			},
			"reports",
		);

		if (reportViewDataArray) {
			this._transport.appState.reportViews ??= [];

			const reports = reportViewDataArray.map((item: ReportDto) => new Report(appState, item));
			this._transport.appState.reportViews = reports.filter(
				(item: {scope: ReportScope; isUserDefined: boolean}) => item.scope === ReportScope.CurrentPortfolio && item.isUserDefined,
			);
		}

		return reportViewDataArray;
	}

	public async markViewAsGlobal(report: Report, skipAddingGlobalViewToRoot = false) {
		const {id: userID} = this._transport.appState.user;

		const {result, error} = await this._transport.requestForOrganization<ReportDto>(
			{
				url: "reports/markasglobal",
				method: XHRLoader.METHOD_POST,
				params: {
					userID,
					reportID: report.id,
				},
			},
			"reports",
		);

		if (result) {
			await this.applyReportUpdate(result, report, skipAddingGlobalViewToRoot);
		}
	}

	public async markViewAsPrivate(report: Report) {
		const {id: userID} = this._transport.appState.user;

		const {result, error} = await this._transport.requestForOrganization<ReportDto>(
			{
				url: "reports/markasprivate",
				method: XHRLoader.METHOD_POST,
				params: {
					userID,
					reportID: report.id,
				},
			},
			"reports",
		);

		if (result) {
			await this.applyReportUpdate(result, report);
		}
	}

	public async getSpacesForReportByPortfolioID(portfolioID: string, organizationID: string): Promise<PortfolioSpacesDto[]> {
		const {result, error} = await this._transport.request<GetAllSpacesForPortfolioQueryResult>(
			{
				url: `data/spaces/all`,
				method: XHRLoader.METHOD_GET,
				params: {
					PortfolioID: portfolioID,
					OrganizationID: organizationID,
				},
			},
			"reports",
		);
		const spaces = result?.spaces;

		if (error) {
			console.error("Error fetching departments:", error);
		}

		if (!spaces || !Array.isArray(spaces)) {
			console.error("Spaces data is not available or not an array");
		}

		return spaces ?? [];
	}

	public async getSutterDepartmentsBySpaceIDs(portfolioID: string, spaceIDs: string[]): Promise<string[]> {
		const {result, error} = await this._transport.request<GetDistinctBoundaryFieldValuesSelectedQueryResult>(
			{
				url: `data/sutterhealth/departments/distinct`,
				method: XHRLoader.METHOD_GET,
				params: {
					portfolioID,
					spaceIDs,
				},
			},
			"reports",
		);
		const boundaries = result?.values;

		if (error) {
			console.error("Error fetching departments:", error);
		}

		if (!boundaries || !Array.isArray(boundaries)) {
			console.error("Departments data is not available or not an array");
		}

		return boundaries ?? [];
	}

	private getReportData(report: Report, data: Partial<ReportDto>): ReportDto {
		const newReportData: ReportDto = {
			...report.data,
			...data,
		};

		newReportData.definition = data.definition ?? report.definition;

		return newReportData;
	}

	private removeReportView(ids: string[]) {
		const appState = this._transport.appState;
		const idsToDelete = new Set(ids);

		appState.reportViews = (appState.reportViews ?? []).filter((view) => !idsToDelete.has(view.id));
	}

	private addReportToGlobalViews(report: Report, skipIfMissingFromStructure: boolean = false) {
		const organization = this._transport.appState.organization;

		if (!organization || !report.isGlobal) {
			return;
		}

		const globalViews = [...organization.globalViews];

		if (!doesItemExistInViewFolderStructure(globalViews, report.id)) {
			if (skipIfMissingFromStructure) {
				return;
			}

			globalViews.push({id: report.id, category: ViewPreferenceCategory.Report});
			organization.setGlobalViews(globalViews);
		}
	}

	private upsertReport(data: ReportDto): Report {
		const appState = this._transport.appState;
		const reportID = data.reportID;
		const isReportView = (data.definition?.scope ?? ReportScope.CurrentPortfolio) === ReportScope.CurrentPortfolio;
		const existingReportView = appState.actions.getReportViewById(reportID);
		const existingReport = appState.lists[XyiconFeature.Report].getById(reportID);
		const report = existingReportView ?? existingReport ?? new Report(appState, data);

		report.applyData(data);

		if (isReportView) {
			if (existingReport) {
				appState.lists[XyiconFeature.Report].deleteByIds([reportID]);
			}

			if (!existingReportView) {
				appState.reportViews ??= [];
				appState.reportViews.push(report);
			}
		} else {
			if (existingReportView) {
				this.removeReportView([reportID]);
			}
		}

		if (!existingReport) {
			appState.lists[XyiconFeature.Report].add(report);
		}

		return report;
	}

	private async applyReportUpdate(data: ReportDto, fallbackReport?: Report, skipAddingGlobalViewToRoot = false): Promise<Report | null> {
		const appState = this._transport.appState;
		const reportData = fallbackReport ? this.getReportData(fallbackReport, data) : data;
		const report = this.upsertReport(reportData);
		const user = appState.user;
		const isReportView = (reportData.definition?.scope ?? ReportScope.CurrentPortfolio) === ReportScope.CurrentPortfolio;
		const sharedWithCurrentUser = !!reportData.reportSharingSettings?.some(
			({userID, userGroupID}) => userID === user.id || (userGroupID && user.userGroupIds.includes(userGroupID)),
		);
		const ownedByCurrentUser = reportData.ownerUserID === user.id;
		const accessibleToCurrentUser = report.isGlobal || ownedByCurrentUser || sharedWithCurrentUser;

		if (!accessibleToCurrentUser) {
			await this._transport.services.view.onModifyActiveView([reportData.reportID], "unshared", report.lastModifiedBy);
			appState.actions.applyDelete([reportData.reportID], XyiconFeature.Report);
			this.applyDeleteReportView([reportData.reportID]);
			window.dispatchEvent(new Event(EventNameForViewsChangeInLocalStorage));

			return null;
		}

		if (isReportView && report.isGlobal) {
			this.addReportToGlobalViews(report, skipAddingGlobalViewToRoot);
		} else {
			appState.organization?.removeElementFromGlobalViews(report.id);
		}

		if (isReportView) {
			user?.updateViewFolderStructures();
		}

		return report;
	}

	public async downloadArchiveReports(report: ReportArchiveDto) {
		const params: GetReportArchiveDownloadRequest = {
			organizationID: this._transport.appState.organizationIdByAuthData,
			userID: this._transport.appState.user.id,
			reportArchiveIDList: [report.reportArchiveID],
		};

		const {result, error} = await this._transport.requestForOrganization<ReportArchiveDownloadDto[]>(
			{
				url: "reports/archives/download",
				method: XHRLoader.METHOD_POST,
				params: params,
			},
			"reports",
		);

		return result;
	}

	public async create<T extends IModel = IModel>(
		data: any,
		feature: XyiconFeature,
		onUploadProgress?: (progress: number) => void,
		cancelToken?: XHRCancelToken,
	): Promise<T[]> {
		const name = FeatureService.getApiNameForFeature(feature);

		const {result, error} = await this._transport.requestForOrganization<T[]>(
			{
				url: `${name}/create`,
				method: XHRLoader.METHOD_PUT,
				params: data,
				onUploadProgress,
				cancelToken,
			},
			feature === XyiconFeature.Report ? "reports" : "base",
		);

		const items: T[] = [];

		if (error) {
			console.log("Error creating error", error);
		} else if (feature === XyiconFeature.Report) {
			const reportDataArray = (Array.isArray(result) ? result : [result]).filter(Boolean) as ReportDto[];

			for (const reportData of reportDataArray) {
				const report = await this.applyReportUpdate(reportData);

				if (report) {
					items.push(report as unknown as T);
				}
			}
		} else {
			if (Array.isArray(result)) {
				items.push(...(this._actions.addMultipleToList(result, feature) as T[]));
			} else {
				items.push(this._actions.addToList(result, feature) as T);
			}
		}

		return items;
	}

	public update(id: string, feature: XyiconFeature, value: any, fieldRefId?: string): Promise<{result: any; error: IError}> {
		const name = FeatureService.getApiNameForFeature(feature);
		const params = this.createUpdateParams(id, fieldRefId, value, feature);

		return this._transport.requestForOrganization({
			url: `${name}/update`,
			method: XHRLoader.METHOD_POST,
			params,
		});
	}

	private createUpdateParams(id: string, fieldRefId: string, value: any, feature: XyiconFeature) {
		const appState = this._transport.appState;
		const spaceId = appState.space ? appState.space.id : "";

		switch (feature) {
			case XyiconFeature.Portfolio:
				return {
					fieldValues: {
						[fieldRefId]: value,
					},
					portfolioIDList: [id],
				};

			case XyiconFeature.Markup:
				return ObjectUtils.apply(
					{
						markupID: id,
						spaceID: spaceId,
						portfolioID: appState.activePortfolioId,
					},
					value,
				);

			case XyiconFeature.Boundary:
				return ObjectUtils.apply(
					{
						boundarySpaceMapID: id,
						portfolioID: appState.activePortfolioId,
						spaceID: spaceId,
					},
					value,
				);

			case XyiconFeature.PermissionSet:
				return {
					portfolioPermissionSetID: id,
					...value,
				};

			case XyiconFeature.PortfolioGroup:
				return {
					portfolioGroupID: id,
					...value,
				};

			case XyiconFeature.UserGroup:
				return {
					userGroupID: id,
					...value,
				};

			case XyiconFeature.User:
				return {
					userID: id,
					...value,
				};

			default:
				console.warn("Feature not found!");
				return null;
		}
	}

	public updateMassFields(items: IModel[], fieldData: {[refId: string]: any}) {
		return this._updateFields(items, fieldData);

		// const promises: Promise<any>[] = [];
		//
		// const itemsByFeatures: FeatureMap<IModel[]> = {};
		// for (const item of items)
		// {
		// 	itemsByFeatures[item.ownFeature] = itemsByFeatures[item.ownFeature] || [];
		// 	itemsByFeatures[item.ownFeature].push(item);
		// }
		//
		// for (const featureString in itemsByFeatures)
		// {
		// 	const items = itemsByFeatures[featureString];
		// 	promises.push(this._updateFields(items, fieldData));
		// }
		//
		// return Promise.all(promises);
	}

	private _updateFields(items: IModel[], fieldData: {[refId: string]: any}) {
		const feature = items[0]?.ownFeature;

		if (!feature) {
			throw new Error("Feature not defined for item!");
		}

		const name = FeatureService.getApiNameForFeature(feature);

		const idListKey = {
			[XyiconFeature.Portfolio]: "portfolioIDList",
			[XyiconFeature.Boundary]: "boundaryIDList",
			[XyiconFeature.XyiconCatalog]: "xyiconCatalogIDList",
			[XyiconFeature.Xyicon]: "xyiconIDList",
			[XyiconFeature.Space]: "spaceIDList",
		}[feature as XyiconFeature.Portfolio | XyiconFeature.XyiconCatalog | XyiconFeature.Space | XyiconFeature.Xyicon | XyiconFeature.Boundary];

		if (!idListKey) {
			throw new Error("Feature not defined for updateFields!");
		}

		const params: UpdateXyiconFieldsRequest | UpdateSpaceFieldsRequest | UpdateBoundaryFieldsRequest = {
			fieldValues: fieldData,
			[idListKey]: items.map((item) => item.id),
		};

		if (feature !== XyiconFeature.Portfolio && !params.portfolioID) {
			for (const item of items) {
				if (item.portfolioId) {
					params.portfolioID = item.portfolioId;
					break;
				}
			}
		}
		if (!params.portfolioID) {
			params.portfolioID = this._transport.appState.activePortfolioId;
		}

		return this._transport.requestForOrganization({
			url: `${name}/updatefields`,
			method: XHRLoader.METHOD_POST,
			params: params,
		});
	}

	public updateXyiconModel(params: UpdateXyiconsModelRequest) {
		return this._transport.requestForOrganization<XyiconDto[]>({
			url: `${FeatureService.getApiNameForFeature(XyiconFeature.Xyicon)}/updatemodel`,
			method: XHRLoader.METHOD_POST,
			params,
		});
	}

	public async updateReport(report: Report) {
		const params: UpdateReportRequest = {
			reportID: report.id,
			ownerUserID: this._transport.appState.user.id,
			...report.serializeData(),
		};

		const response = await this._transport.requestForOrganization<ReportDto>(
			{
				url: "reports/update",
				method: XHRLoader.METHOD_POST,
				params: params,
			},
			"reports",
		);

		if (response.result) {
			await this.applyReportUpdate(response.result, report);
		}

		return response;
	}

	public async saveAndRunReport(report: Report) {
		report.clearReport();
		await this.updateReport(report);
		await this.runReport(report);
	}

	public async runSavedReport(report: Report, customUserInput?: ICustomUserInput) {
		report.clearReport();

		const params: InvokeReportRequest = {
			reportID: report.id,
			userID: this._transport.appState.user.id,
			portfolioID: report.getPortfolio(this._transport.appState),
			customUserInput: customUserInput || null,
		};

		const {result, error} = await this._transport.requestForOrganization<InvokeSavedReportDto>(
			{
				url: "reports/invokereport",
				method: XHRLoader.METHOD_POST,
				params: params,
			},
			"reports",
		);

		if (result) {
			report.addRequestID(result.requestID);
		}

		// In case if SignalR has already responded
		report.completeReportCallback?.();

		return result;
	}

	public async runReport(report: Report) {
		report.clearReport();

		const params: InvokeUnsavedReportRequest = {
			reportID: report.id,
			organizationID: this._transport.appState.organizationIdByAuthData,
			portfolioID: report.portfolioIDList[0] || this._transport.appState.activePortfolioId,
			userID: this._transport.appState.user.id,
			...report.serializeData(),
		};

		const {result, error} = await this._transport.requestForOrganization<InvokeUnsavedReportDto>(
			{
				url: "reports/invokeunsavedreport",
				method: XHRLoader.METHOD_POST,
				params: params,
			},
			"reports",
		);

		report.addRequestID(result.requestID);

		return result;
	}

	// Should be called only by AppActions.deleteItems
	public async deleteItems<T>(items: T[], feature: XyiconFeature, apiNameAddition: "" | "boundaryspacemaps") {
		const name = FeatureService.getApiNameForFeature(feature);
		const params = this.createDeleteParams(items as IModel[], feature, apiNameAddition);
		const isReport = feature === XyiconFeature.Report;

		const {result} = await this._transport.requestForOrganization(
			{
				url: `${name}/delete${apiNameAddition}`,
				method: XHRLoader.METHOD_DELETE,
				params: params,
			},
			isReport ? "reports" : "base",
		);

		if (isReport && result?.deletedList) {
			const user = this._transport.appState.user;
			const isFavoriteReportView = result.deletedList.some((id: string) => this._transport.appState.actions.getReportViewById(id)?.isFavorite);

			await this._transport.services.view.onModifyActiveView(result.deletedList, "deleted", user?.id);
			this.applyDeleteReportView(result.deletedList);
			user?.updateViewFolderStructures();

			if (isFavoriteReportView) {
				await user?.setFavoriteViews(user.favoriteViews);
			}
		}

		return result;
	}

	public applyDeleteReportView(ids: string[]) {
		const appState = this._transport.appState;
		const idsToDelete = new Set(ids);

		this.removeReportView(ids);

		for (const id of idsToDelete) {
			appState.organization?.removeElementFromGlobalViews(id);
			appState.user?.removeElementFromFavoriteViews(id);
		}

		if (idsToDelete.has(appState.selectedReportViewId)) {
			appState.selectedReportViewId = "";
		}
	}

	private createDeleteParams(items: IModel[], feature: XyiconFeature, apiNameAddition: "" | "boundaryspacemaps") {
		const ids = items.map((item) => item.id);
		const portfolioId = this._transport.appState.activePortfolioId;

		switch (feature) {
			case XyiconFeature.Portfolio:
				return {
					portfolioIDList: ids,
				} as DeletePortfolioRequest;

			case XyiconFeature.XyiconCatalog:
				return {
					xyiconCatalogIDList: ids,
				} as DeleteXyiconCatalogRequest;
			// 2025-08-29: As discussed with Chanaka, dashboard delete (DeleteDashboardRequest) is not required for the feature. Hence removed the code.
			case XyiconFeature.Space:
				return {
					portfolioID: portfolioId,
					spaceIDList: ids,
				} as DeleteSpaceRequest;

			case XyiconFeature.SpaceVersion:
				return {
					portfolioID: portfolioId,
					spaceVersionIDList: ids,
				} as DeleteSpaceVersionRequest;

			case XyiconFeature.Markup:
				return {
					spaceID: this._transport.appState.space.id,
					portfolioID: portfolioId,
					markupIDList: ids,
				} as DeleteMarkupRequest;

			case XyiconFeature.Xyicon:
				return {
					xyiconIDList: ids,
					portfolioID: portfolioId,
				} as DeleteXyiconRequest;

			case XyiconFeature.Boundary:
				return apiNameAddition === ""
					? ({
							boundaryIDList: ids,
							portfolioID: portfolioId,
						} as DeleteBoundaryRequest)
					: ({
							boundarySpaceMapIDList: ids,
							portfolioID: portfolioId,
						} as DeleteBoundarySpaceMapRequest);

			case XyiconFeature.LibraryImage:
				return {
					libraryImageIDList: ids,
				} as DeleteLibraryImagesRequest;

			case XyiconFeature.LibraryModel:
				return {
					libraryModelIDList: ids,
				} as DeleteLibraryModelsRequest;

			case XyiconFeature.PortfolioGroup:
				return {
					portfolioGroupIDList: ids,
				} as DeletePortfolioGroupRequest;

			case XyiconFeature.UserGroup:
				return {
					userGroupIDList: ids,
				} as DeleteUserGroupRequest;

			case XyiconFeature.User:
				return {
					userIDList: ids.map((id: string) => ({
						userIDToBeDeleted: id,
					})),
				} as DeleteUsersRequest;

			case XyiconFeature.PermissionSet:
				return {
					portfolioPermissionSetIDList: ids,
				} as DeletePortfolioPermissionSetRequest;

			case XyiconFeature.Document:
			case XyiconFeature.OrganizationDocument:
			case XyiconFeature.PortfolioDocument:
				return {
					documentIDList: ids,
				} as DeleteDocumentsRequest;

			case XyiconFeature.Report:
				return {
					reportIDList: ids,
					userID: this._transport.appState.user?.id, // this is actually not needed only according to swagger
				} as DeleteReportRequest;

			default:
				console.warn("Feature not found!");
				return null;
		}
	}

	public clearPromises() {
		this._promises = {};
		this._dependencyPromises = {};

		// Cancel all ongoing XHRs, to avoid synch related issues:
		// - when switching portfolios quickly, earlier requests might complete before later ones,
		// causing inconsistent data
		// - these ongoing requests are not useful for the new portfolio anyway
		this._cancelTokens.forEach((cancelToken) => {
			cancelToken.cancel();
		});
		this._cancelTokens.clear();
	}

	private async loadDependencies(feature: XyiconFeature, portfolioId: string = this._transport.appState.activePortfolioId ?? ""): Promise<void> {
		// load dependencies
		const featuresToLoad = this._actions.getLoadingDependencies(feature).filter((ft: XyiconFeature) => {
			return ft !== feature && !this._transport.appState.lists[ft].loaded;
		});

		for (const featureToLoad of featuresToLoad) {
			if (!this._dependencyPromises[featureToLoad]) {
				// Don't change the order of this, otherwise you can end up in an endless loop
				this._dependencyPromises[featureToLoad] = this.refreshList(featureToLoad, false, portfolioId);
			}
		}

		await Promise.all(featuresToLoad.map((ft) => this._dependencyPromises[ft]));
	}

	public async refreshList<T extends IModel = any>(feature: XyiconFeature, force: boolean = false, propPortfolioId?: string): Promise<IModel[]> {
		if (force) {
			this._promises[feature] = undefined;
			this._transport.appState.lists[feature].clear();
		}

		if (!this._promises[feature]) {
			let trueResolveFunction: (value: IModel[] | PromiseLike<IModel[]>) => void;
			let trueRejectFunction: (reason?: any) => void;
			// Workaround to assign the promise value immediately to this variable, otherwise it can be started multiple times because of the asynchronous nature
			this._promises[feature] = new Promise<IModel[]>((resolve, reject) => {
				trueResolveFunction = resolve;
				trueRejectFunction = reject;
			});

			const appState = this._transport.appState;
			const portfolioId = propPortfolioId ?? appState.relevantPortfolioId;

			await this.loadDependencies(feature, portfolioId);

			const collection = appState.lists[feature];

			if (collection.loaded) {
				this.onListLoaded(feature);
				trueResolveFunction(collection.array);
			} else {
				const organizationID = this._transport.services.auth.authData.organizationID;

				if (organizationID) {
					const name = FeatureService.getApiNameForFeature(feature, true);

					// For some requests, portfolioId would not be needed (usergroups/all, users/all)
					// TODO
					const isOrganizationWideList = [
						XyiconFeature.Organization,
						XyiconFeature.OrganizationDocument,
						XyiconFeature.User,
						XyiconFeature.ExternalUser,
						XyiconFeature.UserGroup,
						XyiconFeature.PortfolioGroup,
						XyiconFeature.PermissionSet,
						XyiconFeature.Report,
						XyiconFeature.XyiconCatalog,
						XyiconFeature.LibraryImage,
						XyiconFeature.LibraryModel,
						XyiconFeature.Portfolio,
						XyiconFeature.Dashboard,
					].includes(feature);

					if (!isOrganizationWideList && !portfolioId) {
						console.warn(`PortfolioId is not set to fetch ${XyiconFeature[feature]}!`);
						// Not organizationwide list and portfolioId is not set -> can't retrieve list
						collection.loaded = true;
						trueResolveFunction(undefined);
						return;
					}

					const cancelToken = new XHRCancelToken();

					this._cancelTokens.add(cancelToken);

					const params: {
						OrganizationID: string;
						PortfolioID?: string;
						UserID?: string;
					} = {
						OrganizationID: organizationID,
						...(isOrganizationWideList ? undefined : {PortfolioID: portfolioId}),
					};

					if (appState.user?.id) {
						// For reports
						params.UserID = appState.user?.id;
					}

					const {result: dataArray, error} = await this._transport.get<PortfolioDto[] | IDefaultGlyphData[]>({
						url: `${name}/all`,
						params,
						apiName: feature === XyiconFeature.Report ? "reports" : "base",
						cancelToken: cancelToken,
					});

					this._cancelTokens.delete(cancelToken);

					const array: T[] = [];

					if (dataArray) {
						// Do not call collection.loaded yet because FilterActions will get confused
						// (collection is loaded but no data yet causing a performance problem, where filters
						// are being calculated twice on app load during initial list loadings).
						// (Other way to fix performance issue is to check collection.version too in FilterActions).
						// collection.loaded = true;
						let filteredDataArray = dataArray;
						if (feature === XyiconFeature.Space) {
							filteredDataArray = filteredDataArray.filter((data) => {
								const perm = this._actions.getModuleTypePermission((data as SpaceDto).spaceTypeID, XyiconFeature.Space, portfolioId);

								return perm > Permission.None;
							}) as PortfolioDto[] | IDefaultGlyphData[];
						}

						for (const data of filteredDataArray) {
							const item = FeatureService.createModel(data, feature, appState) as T;

							if (item) {
								array.push(item);
							}
						}
						// This sets collection.loaded = true
						collection.replaceByArray(array);
					}

					this.onListLoaded(feature);
					if (dataArray) {
						trueResolveFunction(collection.array);
					} else {
						trueRejectFunction({
							result: error,
						});
					}
				} else {
					trueRejectFunction({
						result: new Error("OrganizationID is not set!"),
					});
				}
			}
		}

		return this._promises[feature];
	}

	private onListLoaded = (feature: XyiconFeature) => {
		if (feature === XyiconFeature.User) {
			const appState = this._transport.appState;
			const collection = appState.lists[feature];

			appState.user = collection.getById(this._transport.services.auth.authData.userID);
		}
	};

	public static createModel(data: any, feature: XyiconFeature, appState: AppState): IModel {
		if (!data) {
			console.warn("Invalid data!");
			return null;
		}
		switch (feature) {
			case XyiconFeature.Portfolio:
				return new Portfolio(data, appState);
			case XyiconFeature.XyiconCatalog:
				return new Catalog(data, appState);
			case XyiconFeature.Dashboard:
				return new Dashboard(data, appState);
			case XyiconFeature.Space:
				return new Space(data, appState);
			case XyiconFeature.SpaceVersion:
				return new SpaceVersion(data);
			case XyiconFeature.Markup:
				return new Markup(data, appState);
			case XyiconFeature.Boundary:
				return new Boundary(data, appState);
			case XyiconFeature.Xyicon:
				return new Xyicon(data, appState);
			case XyiconFeature.LibraryImage:
				return new LibraryImage(data, appState);
			case XyiconFeature.LibraryModel:
				return new LibraryModel(data, appState);
			case XyiconFeature.PortfolioGroup:
				return new PortfolioGroup(data);
			case XyiconFeature.UserGroup:
				return new UserGroup(data);
			case XyiconFeature.User:
				return new User(data, appState);
			case XyiconFeature.ExternalUser:
				return new ExternalUser(data, appState);
			case XyiconFeature.PermissionSet:
				return new PermissionSet(data);
			case XyiconFeature.Document:
			case XyiconFeature.OrganizationDocument:
			case XyiconFeature.PortfolioDocument:
				return new DocumentModel(data, feature, appState);
			case XyiconFeature.Report:
				return new Report(appState, data);
			case XyiconFeature.Link:
				return new Link(data);

			default:
				console.warn("Feature not found!");
				return null;
		}
	}

	private get _actions() {
		return this._transport.appState.actions;
	}
}
